import { delay, fork, select } from "redux-saga/effects";
import { call, put } from "redux-saga/effects";
import { selectUser } from "../../features/auth/authSlice";
import { User } from "../../types/User";
import { Repo } from "../../types/Repo";
import {
  FunctionInfo,
  NetworkEdge,
  NetworkNode,
  NetworkPov,
  SearchResult,
  addNetworkElements,
  selectNetworkOpen,
  selectNetworkRepoId,
  setError as setDashError,
  setStatus as setDashStatus,
  setFileFolderLabel,
  setFileFolderLabels,
  setFileFolders,
  setFileLineCounts,
  setFileListPagination,
  setFileModifieds,
  setFileSizes,
  setFiles,
  setFuncMapCallees,
  setFuncMapError,
  setFuncMapResult,
  setFuncMapSlice,
  setFuncMapSliceError,
  setGitInfo,
  setGitOutput,
  setIndexStatus,
  setIsFileListRefreshing,
  setNetworkError,
  setNetworkGraph,
  setNetworkHiddenDirs,
  setNetworkNodeColors,
  setNetworkPov,
  openNetworkCode,
  setNetworkCode,
  setNetworkCodeError,
  setRepoId,
  setRepos,
  setReposMode,
  setSearchMode,
  setSearchResult,
  updateDashLocal,
} from "../../features/library/librarySlice";
import * as actions from "../actions";

import {
  apiCommitRepo,
  apiCreateFile,
  apiCreateRepo,
  apiCreateRepoFolder,
  apiDeleteFile,
  apiGetCodeSearch,
  apiGetFileList,
  apiGetGitInfo,
  apiGetLineCounts,
  apiGetRepos,
  apiOpenFile,
  apiPullRepo,
  apiPushRepo,
  apiRenameFile,
  apiSetFolderLabel,
} from "../../services/RepoAPI";
import {
  apiFeatureSlice,
  apiFileCode,
  apiFileFunctions,
  apiFileGraph,
  apiFunctionCallees,
  apiFunctionCallers,
  apiFunctionSlice,
  apiGetGraphSettings,
  apiIndexBackground,
  apiIndexStatus,
  apiMapFile,
  apiMethodCode,
  apiReverseSlice,
  apiSaveGraphSettings,
} from "../../services/StructuresAPI";
import { apiUpdateDashFile } from "../../services/DashAPI";

export function* createRepo(
    action: ReturnType<typeof actions.createRepo>
    ) {
    yield put(setDashStatus({ status: "loading", error: undefined }));

    try {
        const userState = (yield select(selectUser)) as User;
        const createResponse = yield call(
            apiCreateRepo,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload
            );

        yield put(actions.getRepos());

    } catch (e) {
        yield put(setDashError(String(e)));
        return;
    }
}

export function* getRepos(
    action: ReturnType<typeof actions.getRepos>
    ) {
    yield put(setDashStatus({ status: "loading", error: undefined }));
    
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiGetRepos,
            userState.default_organization_id,
            userState.default_workspace_id
            );

        console.log(response.data);

        yield put(setRepos(response.data.repos));

        yield put(setDashStatus({ status: "idle", error: undefined }));
    } catch (e) {
        yield put(setDashError(String(e)));
        return;
    }
}

export function* pullRepo(
    action: ReturnType<typeof actions.pullRepo>
    ) {

    yield put(setDashStatus({ status: "loading", error: undefined }));
    yield put(setGitOutput(undefined));
    
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiPullRepo,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload
            );

        // Check if response has stdout and stderr fields
        if (response.data && (response.data.stdout !== undefined || response.data.stderr !== undefined)) {
          yield put(setGitOutput({
            stdout: response.data.stdout || "",
            stderr: response.data.stderr || ""
          }));
        }

       // yield put(setDashStatus({ status: "idle", error: "Pull complete" }));
    } catch (e) {
      yield put(setDashError(String(e)));
      return;
    }
}

export function* commitRepo(
    action: ReturnType<typeof actions.commitRepo>
    ) {
    console.log("here push");

    yield put(setDashStatus({ status: "loading", error: undefined }));
    yield put(setGitOutput(undefined));
    
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiCommitRepo,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.message,
            );

        // Check if response has stdout and stderr fields
        if (response.data && (response.data.stdout !== undefined || response.data.stderr !== undefined)) {
          yield put(setGitOutput({
            stdout: response.data.stdout || "",
            stderr: response.data.stderr || ""
          }));
        }

        //yield put(setDashStatus({ status: "idle", error: "Commit complete" }));
    } catch (e) {
      yield put(setDashError(String(e)));
      return;
    }
}

export function* pushRepo(
    action: ReturnType<typeof actions.pullRepo>
    ) {
    console.log("here push");

    yield put(setDashStatus({ status: "loading", error: undefined }));
    yield put(setGitOutput(undefined));
    
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiPushRepo,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload
            );

        // Check if response has stdout and stderr fields
        if (response.data && (response.data.stdout !== undefined || response.data.stderr !== undefined)) {
          yield put(setGitOutput({
            stdout: response.data.stdout || "",
            stderr: response.data.stderr || ""
          }));
        }

        //yield put(setDashStatus({ status: "idle", error: "Push complete" }));
    } catch (e) {
      yield put(setDashError(String(e)));
      return;
    }
}

export function* fileList(
    action: ReturnType<typeof actions.fileList>
    ) {
    yield put(setDashStatus({ status: "loading", error: undefined }));
    yield put(setIsFileListRefreshing(true));

    try {
        yield put(setFiles([]));
        yield put(setFileSizes([]));
        yield put(setFileModifieds([]));
        yield put(setFileFolders([]));

        const userState = (yield select(selectUser)) as User;
        const page_start = action.payload.page_start ?? 0;
        const page_size = action.payload.page_size ?? 20;
        const filter = action.payload.filter ?? "";
        const response = yield call(
            apiGetFileList,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.id,
            action.payload.folder,
            page_start,
            page_size,
            filter,
            );

        yield put(setFiles(response.data.files));
        yield put(setFileSizes(response.data.file_sizes));
        yield put(setFileModifieds(response.data.file_modifieds));
        yield put(setFileFolders(response.data.folders));
        yield put(setFileFolderLabels(response.data.folder_labels ?? {}));
        yield put(setFileListPagination({
          full_length: response.data.full_length ?? response.data.files?.length ?? 0,
          page_start,
          page_size,
        }));

        yield put(setDashStatus({ status: "idle", error: undefined }));
        yield put(setIsFileListRefreshing(false));
    } catch (e) {
      yield put(setDashError(String(e)));
      yield put(setIsFileListRefreshing(false));
      return;
    }
}

export function* setFolderLabel(
    action: ReturnType<typeof actions.setFolderLabel>
    ) {
    try {
        const userState = (yield select(selectUser)) as User;
        yield call(
            apiSetFolderLabel,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.folderPath,
            action.payload.label,
            );

        // Update the local label map so the border reflects the change immediately.
        yield put(setFileFolderLabel({ path: action.payload.folderPath, label: action.payload.label }));
    } catch (e) {
      yield put(setDashError(String(e)));
      return;
    }
}

export function* getLineCounts(
    action: ReturnType<typeof actions.getLineCounts>
    ) {
    // Lazy background task: do not toggle the global loading state.
    try {
        if (!action.payload.files || action.payload.files.length === 0) return;

        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiGetLineCounts,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.files,
            );

        yield put(setFileLineCounts(response.data.line_counts ?? {}));
    } catch (e) {
      // Silent on failure — this is a non-essential background enrichment.
      return;
    }
}

export function* mapFile(
    action: ReturnType<typeof actions.mapFile>
    ) {
    // The component sets status to "mapping" (via openFuncMap) before dispatching.
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiMapFile,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.filePath,
            );

        yield put(setFuncMapResult({
            file_path: response.data.file_path,
            stats: response.data.stats ?? null,
            mapped_files: response.data.mapped_files ?? [],
            functions: response.data.functions ?? [],
        }));
    } catch (e) {
      yield put(setFuncMapError(String(e)));
      return;
    }
}

export function* getFunctionCallees(
    action: ReturnType<typeof actions.getFunctionCallees>
    ) {
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiFunctionCallees,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.methodId,
            );

        yield put(setFuncMapCallees({
            methodId: action.payload.methodId,
            callees: response.data.callees ?? [],
        }));
    } catch (e) {
      // Non-fatal: leave an empty expansion rather than failing the whole map view.
      yield put(setFuncMapCallees({ methodId: action.payload.methodId, callees: [] }));
      return;
    }
}

export function* getFeatureSlice(
    action: ReturnType<typeof actions.getFeatureSlice>
    ) {
    // The component sets the slice to "loading" before dispatching.
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiFeatureSlice,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.methodId,
            );

        const blocks = (response.data.blocks ?? []).slice();
        // Sort descending by distance (depth): farthest dependencies first,
        // the sliced function (depth 0) last.
        blocks.sort((a: any, b: any) => (b.depth ?? 0) - (a.depth ?? 0));
        yield put(setFuncMapSlice(blocks));
    } catch (e) {
      yield put(setFuncMapSliceError());
      return;
    }
}

export function* getReverseSlice(
    action: ReturnType<typeof actions.getReverseSlice>
    ) {
    // The component sets the slice to "loading" before dispatching.
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiReverseSlice,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.methodId,
            );

        const blocks = (response.data.blocks ?? []).slice();
        // Sort descending by distance: farthest callers first, the function (depth 0) last.
        blocks.sort((a: any, b: any) => (b.depth ?? 0) - (a.depth ?? 0));
        yield put(setFuncMapSlice(blocks));
    } catch (e) {
      yield put(setFuncMapSliceError());
      return;
    }
}

// --- Dependency network chart ---

// Basename of a repo-relative path, for compact file-node labels.
function fileBase(path: string | null | undefined): string {
  if (!path) return "(unknown)";
  const parts = path.split("/");
  return parts[parts.length - 1] || path;
}

function functionLabel(f: FunctionInfo): string {
  return f.class_name ? `${f.class_name}::${f.name}` : f.name;
}

function functionToNode(f: FunctionInfo): NetworkNode {
  return {
    id: `m${f.method_id}`,
    kind: "function",
    label: functionLabel(f),
    methodId: f.method_id,
    filePath: f.file_path,
    lineNumber: f.line_number,
    usage: f.inbound ?? 0,
    fanout: f.outbound ?? 0,
    transitiveIn: f.transitive_inbound ?? 0,
    transitiveOut: f.transitive_outbound ?? 0,
    weight: (f.outbound ?? 0) + (f.inbound ?? 0),
  };
}

// Initial file-level graph load (file nodes + aggregated cross-file edges).
export function* loadFileGraph(
    action: ReturnType<typeof actions.loadFileGraph>
    ) {
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiFileGraph,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            );

        // Accept both the compact array form (node = [file_id, path, method_count,
        // usage, fanout]; edge = [src, dst, weight]) and the older object form, so
        // the frontend works regardless of which side was deployed first.
        const nodes: NetworkNode[] = (response.data.nodes ?? []).map((n: any) => {
            const id = Array.isArray(n) ? n[0] : n?.file_id;
            const path = (Array.isArray(n) ? n[1] : n?.path) ?? "";
            const methods = (Array.isArray(n) ? n[2] : n?.method_count) ?? 0;
            const usage = (Array.isArray(n) ? n[3] : n?.usage) ?? 0;
            const fanout = (Array.isArray(n) ? n[4] : n?.fanout) ?? 0;
            const tIn = (Array.isArray(n) ? n[5] : n?.transitive_in) ?? 0;
            const tOut = (Array.isArray(n) ? n[6] : n?.transitive_out) ?? 0;
            return {
                id: `f${id}`,
                kind: "file",
                label: fileBase(path),
                fileId: id,
                filePath: path || null,
                methodCount: methods,
                usage,
                fanout,
                transitiveIn: tIn,
                transitiveOut: tOut,
                weight: usage,
            };
        });
        const edges: NetworkEdge[] = (response.data.edges ?? []).map((e: any) => {
            const s = Array.isArray(e) ? e[0] : e?.source_file_id;
            const t = Array.isArray(e) ? e[1] : e?.target_file_id;
            const w = (Array.isArray(e) ? e[2] : e?.weight) ?? 1;
            return { source: `f${s}`, target: `f${t}`, weight: w };
        });

        yield put(setNetworkGraph({ nodes, edges }));
    } catch (e) {
        yield put(setNetworkError(String(e)));
        return;
    }
}

// Enqueue a background (re)index, then poll for completion.
export function* indexRepoBackground(
    action: ReturnType<typeof actions.indexRepoBackground>
    ) {
    try {
        const userState = (yield select(selectUser)) as User;
        yield call(
            apiIndexBackground,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.jobType ?? "INCREMENTAL",
            action.payload.changedFiles,
            );
        yield put(actions.pollIndexStatus({ repoId: action.payload.repoId, untilDone: true }));
    } catch (e) {
        // Non-fatal: indexing is best-effort background work.
        return;
    }
}

// Poll index_status. With untilDone, loop (capped) until the job settles, then
// refresh the file graph if the network view is open for this repo.
export function* pollIndexStatus(
    action: ReturnType<typeof actions.pollIndexStatus>
    ) {
    const userState = (yield select(selectUser)) as User;
    const repoId = action.payload.repoId;
    const untilDone = action.payload.untilDone ?? false;
    // Cap the poll loop generously — a first full index of a huge repo can run
    // for many minutes. ~600 polls at ~1.2s ≈ 12 min of active indexing.
    const maxPolls = untilDone ? 600 : 1;

    for (let i = 0; i < maxPolls; i++) {
        let status: any;
        try {
            const response = yield call(
                apiIndexStatus,
                userState.default_organization_id,
                userState.default_workspace_id,
                repoId,
                );
            status = response.data;
            yield put(setIndexStatus(status));
        } catch (e) {
            return;
        }

        if (!untilDone) return;

        if (status.status === "completed" || status.status === "failed" || status.status === "none") {
            // Refresh the base graph once indexing settles, but only if the user is
            // looking at the network for this repo (don't clobber their expansions otherwise).
            const networkOpen = (yield select(selectNetworkOpen)) as boolean;
            const networkRepoId = (yield select(selectNetworkRepoId)) as number;
            if (status.status === "completed" && networkOpen && networkRepoId === repoId) {
                yield put(actions.loadFileGraph({ repoId }));
            }
            return;
        }

        // Poll quickly while the worker is actively running so the progress feed
        // feels live; back off a little while merely queued.
        yield delay(status.status === "running" ? 1200 : 2500);
    }
}

// Expand a file node into its function nodes (containment edges file -> function).
export function* expandNetworkFile(
    action: ReturnType<typeof actions.expandNetworkFile>
    ) {
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiFileFunctions,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.filePath,
            );

        const fns: FunctionInfo[] = response.data.functions ?? [];
        const fileNodeId = `f${action.payload.fileId}`;
        const nodes: NetworkNode[] = fns.map(functionToNode);
        const edges: NetworkEdge[] = nodes.map((n) => ({ source: fileNodeId, target: n.id, weight: 1 }));

        yield put(addNetworkElements({ nodes, edges, expandedFileId: action.payload.fileId }));
    } catch (e) {
        // Non-fatal: leave the file node un-expanded.
        return;
    }
}

// Expand a function node into its callee functions (call edges, crossing files).
export function* expandNetworkFunction(
    action: ReturnType<typeof actions.expandNetworkFunction>
    ) {
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiFunctionCallees,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.methodId,
            );

        const callees: FunctionInfo[] = response.data.callees ?? [];
        const fromId = `m${action.payload.methodId}`;
        const nodes: NetworkNode[] = callees.map(functionToNode);
        const edges: NetworkEdge[] = nodes.map((n) => ({ source: fromId, target: n.id, weight: 1 }));

        yield put(addNetworkElements({ nodes, edges, expandedFunctionId: action.payload.methodId }));
    } catch (e) {
        // Non-fatal.
        return;
    }
}

// Expand a function node into its caller functions (upstream; edges point from
// each caller to the selected function, crossing files).
export function* expandNetworkCallers(
    action: ReturnType<typeof actions.expandNetworkCallers>
    ) {
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiFunctionCallers,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.methodId,
            );

        const callers: FunctionInfo[] = response.data.callers ?? [];
        const toId = `m${action.payload.methodId}`;
        const nodes: NetworkNode[] = callers.map(functionToNode);
        const edges: NetworkEdge[] = nodes.map((n) => ({ source: n.id, target: toId, weight: 1 }));

        yield put(addNetworkElements({ nodes, edges, expandedFunctionId: action.payload.methodId }));
    } catch (e) {
        // Non-fatal.
        return;
    }
}

// Program slice: every function transitively called by this one (across files),
// plus the dependency edges among them — added to the graph as a subgraph.
export function* expandNetworkSlice(
    action: ReturnType<typeof actions.expandNetworkSlice>
    ) {
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiFunctionSlice,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.methodId,
            );

        const infos: FunctionInfo[] = response.data.nodes ?? [];
        const nodes: NetworkNode[] = infos.map(functionToNode);
        const edges: NetworkEdge[] = (response.data.edges ?? []).map((e: any) => ({
            source: `m${e[0]}`,
            target: `m${e[1]}`,
            weight: 1,
        }));

        yield put(addNetworkElements({ nodes, edges, expandedFunctionId: action.payload.methodId }));
    } catch (e) {
        // Non-fatal.
        return;
    }
}

// Fetch source for the "Show code" modal (a function's block, or a whole file).
export function* showNetworkCode(
    action: ReturnType<typeof actions.showNetworkCode>
    ) {
    yield put(openNetworkCode({ title: action.payload.title, subtitle: action.payload.subtitle }));
    try {
        const userState = (yield select(selectUser)) as User;
        let body = "";
        if (action.payload.methodId !== undefined) {
            const response = yield call(
                apiMethodCode,
                userState.default_organization_id,
                userState.default_workspace_id,
                action.payload.repoId,
                action.payload.methodId,
                );
            body = response.data.code ?? "// (no source available)";
        } else if (action.payload.filePath) {
            const response = yield call(
                apiFileCode,
                userState.default_organization_id,
                userState.default_workspace_id,
                action.payload.repoId,
                action.payload.filePath,
                );
            body = response.data.code ?? "// (could not read file)";
        }
        yield put(setNetworkCode(body));
    } catch (e) {
        yield put(setNetworkCodeError());
        return;
    }
}

// Load persisted graph settings (sizing POV + hidden dirs) for the repo.
export function* loadGraphSettings(
    action: ReturnType<typeof actions.loadGraphSettings>
    ) {
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiGetGraphSettings,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            );
        const pov = (response.data.pov ?? "usage") as NetworkPov;
        yield put(setNetworkPov(pov));
        yield put(setNetworkHiddenDirs(response.data.hidden_dirs ?? []));
        yield put(setNetworkNodeColors(response.data.node_colors ?? {}));
    } catch (e) {
        // Non-fatal: fall back to defaults already in state.
        return;
    }
}

// Persist graph settings for the repo.
export function* saveGraphSettings(
    action: ReturnType<typeof actions.saveGraphSettings>
    ) {
    try {
        const userState = (yield select(selectUser)) as User;
        yield call(
            apiSaveGraphSettings,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.pov,
            action.payload.hiddenDirs,
            action.payload.nodeColors,
            );
    } catch (e) {
        return;
    }
}

export function* gitInfo(
    action: ReturnType<typeof actions.gitInfo>
    ) {
 
    yield put(setDashStatus({ status: "loading", error: undefined }));
    
    try {
        yield put(setGitInfo(""));

        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiGetGitInfo,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.id,
            action.payload.folder,
            action.payload.requestType,
            action.payload.count,
            );

        console.log(response.data);

        yield put(setGitInfo(response.data.stdout));

        yield put(setDashStatus({ status: "idle", error: undefined }));
    } catch (e) {
      yield put(setDashError(String(e)));
      return;
    }
}

export function* codeSearch(
    action: ReturnType<typeof actions.codeSearch>
    ) {
 
    yield put(setDashStatus({ status: "loading", error: undefined }));
    
    try {
        //yield put(setFiles([]));
        //yield put(setFileFolders([]));

        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiGetCodeSearch,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.folder,
            action.payload.searchString,
            );

        console.log(response.data);

        yield put(setSearchMode(true));
        yield put(setSearchResult(response.data.files.map((_: string) => ({
            path: _
        }))
        ));

        //yield put(setFiles(response.data.files));
        //yield put(setFileFolders(response.data.folders));

        //yield put(setDashStatus({ status: "idle", error: undefined }));
    } catch (e) {
      yield put(setDashError(String(e)));
      return;
    }
}

export function* openFile(
    action: ReturnType<typeof actions.openFile>
    ) {

    yield put(setDashStatus({ status: "loading", error: undefined }));
    
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiOpenFile,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.file_name,
            );

        //console.log(response.data.dash_id);

        //yield(put(setRepoId(-1)));
        //yield(put(setReposMode(false)));
        

        yield(put(actions.setEditingDash(response.data.dash)));

        yield put(setDashStatus({ status: "idle", error: undefined }));
    } catch (e) {
      yield put(setDashError(String(e)));
      return;
    }
}

export function* deleteFile(
    action: ReturnType<typeof actions.deleteFile>
    ) {

    yield put(setDashStatus({ status: "loading", error: undefined }));
    
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiDeleteFile,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.file_name,
            );

        // Refresh the file list - extract folder from file_name
        const folder = action.payload.file_name.includes("/") 
          ? action.payload.file_name.substring(0, action.payload.file_name.lastIndexOf("/"))
          : "/";
        
        yield(put(actions.fileList({id: action.payload.repoId, folder: folder})));

        yield put(setDashStatus({ status: "idle", error: undefined }));
    } catch (e) {
      yield put(setDashError(String(e)));
      return;
    }
}

export function* renameFile(
    action: ReturnType<typeof actions.renameFile>
    ) {

    yield put(setDashStatus({ status: "loading", error: undefined }));
    
    try {
        const userState = (yield select(selectUser)) as User;
        const response = yield call(
            apiRenameFile,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.file_name,
            action.payload.new_repo_file_name,
            );

        // Refresh the file list - extract folder from original file_name
        const folder = action.payload.file_name.includes("/") 
          ? action.payload.file_name.substring(0, action.payload.file_name.lastIndexOf("/"))
          : "/";
        
        yield(put(actions.fileList({id: action.payload.repoId, folder: folder})));

        yield put(setDashStatus({ status: "idle", error: undefined }));
    } catch (e) {
      yield put(setDashError(String(e)));
      return;
    }
}

export function* createRepoFile(
    action: ReturnType<typeof actions.createRepoFile>
    ) {
    //console.log("here, create file");

    yield put(setDashStatus({ status: "loading", error: undefined }));
    
    try {
        const userState = (yield select(selectUser)) as User;
        const response1 = yield call(
            apiCreateFile,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.file_name,
            );

        console.log(response1.data.dash_id);

        const response2 = yield call(
            apiOpenFile,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.file_name,
            );

        if (action.payload.initialCode != null && action.payload.initialCode !== "" && response2.data?.dash?.id != null) {
            yield call(
                apiUpdateDashFile,
                userState.default_organization_id,
                userState.default_workspace_id,
                response2.data.dash.id,
                action.payload.initialCode,
            );
        }

        yield(put(actions.fileList({id: action.payload.repoId, folder: action.payload.file_path})));

        yield(put(actions.setEditingDash(response2.data.dash)));

        yield put(setDashStatus({ status: "idle", error: undefined }));
    } catch (e) {
      yield put(setDashError(String(e)));
      return;
    }
}

export function* createRepoFolder(
    action: ReturnType<typeof actions.createRepoFolder>
    ) {

    yield put(setDashStatus({ status: "loading", error: undefined }));
    
    try {
        const userState = (yield select(selectUser)) as User;
        const response1 = yield call(
            apiCreateRepoFolder,
            userState.default_organization_id,
            userState.default_workspace_id,
            action.payload.repoId,
            action.payload.folder_name,
            );

        yield(put(actions.fileList({id: action.payload.repoId, folder: action.payload.file_path})));

        yield put(setDashStatus({ status: "idle", error: undefined }));
    } catch (e) {
      yield put(setDashError(String(e)));
      return;
    }
}